import dayjs, { UnitType } from "dayjs"
import isToday from "dayjs/plugin/isToday"
import weekday from "dayjs/plugin/weekday"
import isBetween from "dayjs/plugin/isBetween"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import advancedFormat from "dayjs/plugin/advancedFormat"
import weekOfYear from "dayjs/plugin/weekOfYear"
import updateLocale from "dayjs/plugin/updateLocale"
import { pipe } from "fp-ts/lib/function"
import * as A from "fp-ts/Array"
import * as O from "fp-ts/Option"

dayjs.extend(updateLocale)
dayjs.extend(isToday)
dayjs.extend(weekday)
dayjs.extend(weekOfYear)
dayjs.extend(advancedFormat)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)

dayjs.updateLocale("en", {
  weekStart: 1, //  set the start of a week to Monday
})

const MONTH_CALENDAR_DURATION_IN_DAYS = 42
export type Calendar = {
  current: Date
  title: string
  weekdays: string[]
  entries: CalendarEntry[]
}

export type CalendarEntry = {
  date: string
  isToday: boolean
  disabled: boolean
}

export const monthlyCalendar = (
  from: Date,
  durationInDays: number = MONTH_CALENDAR_DURATION_IN_DAYS
): Calendar => {
  const inputDate = dayjs(from).startOf("month")

  if (dayjs(inputDate).isValid()) {
    return {
      current: new Date(inputDate.format("YYYY-MM-DD")),
      title: inputDate.format("MMMM YYYY"),
      weekdays: Array.from({ length: 7 }).map((_, idx) =>
        dayjs().weekday(idx).format("ddd")
      ),
      entries: Array.from({ length: durationInDays }).map((_, idx) => {
        const currentDate = dayjs(inputDate)
          .startOf("month")
          .startOf("week")
          .add(idx, "day")
          .format("YYYY-MM-DD")
        return {
          date: currentDate,
          disabled: dayjs(currentDate).month() !== inputDate.month(),
          isToday: dayjs(currentDate).isToday(),
        }
      }),
    }
  } else throw new TypeError("Invalid Date")
}

export const nextMonthCalendar = (date: Date): Calendar =>
  monthlyCalendar(dayjs(date).add(1, "month").toDate())

export const prevMonthCalendar = (date: Date): Calendar =>
  monthlyCalendar(dayjs(date).subtract(1, "month").toDate())

export const dayOfMonth = (date: string): string => {
  if (new Date(date)) {
    return dayjs(date).format("ddd Do")
  } else throw new TypeError("Invalid Date")
}

export const upcommingDates = (dates: Date[], unit: "week" | "month"): Date[] =>
  pipe(
    dates,
    A.filterMap(date =>
      dayjs(date).isBetween(dayjs(), dayjs().endOf(unit), "day", "[]")
        ? O.some(date)
        : O.none
    )
  )

export const isNext = (date: Date, unit: UnitType): boolean =>
  dayjs(dayjs(date)).isAfter(new Date(), unit)

export const isSame = (date: Date, unit: UnitType): boolean =>
  dayjs(dayjs(date)).isSame(new Date(), unit)

export const isBefore = (date: Date, unit: UnitType): boolean =>
  dayjs(dayjs(date)).isBefore(new Date(), unit)

export const sameOrAfter = (date: Date, unit: UnitType): boolean =>
  dayjs(dayjs(date)).isSameOrAfter(new Date(), unit)
